// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Epic Cloud Specifications Guide (CSG) — target state vs the current estate
// ---------------------------------------------------------------------------
// A client hands Optimum two artefacts that have never been read side by side:
//
//   1. an RVTools export of the whole VMware estate (thousands of VMs, already
//      imported here as ITComponent fact sheets by Infrastructure Import), and
//   2. Epic's Cloud Specifications Guide workbooks — one per target environment
//      (e.g. an AWS build and an Isolated Recovery Environment), whose sheets
//      are Assumptions / Scope / Compute Bill of Materials / Storage Bill of
//      Materials / Appendix.
//
// THE SCOPING FACT THAT GOVERNS THIS WHOLE SCREEN:
//   The CSG is a bill of materials for the EPIC FOOTPRINT ONLY. It is not a
//   target state for the whole estate. Presenting CSG totals as though they
//   cover every VM in the inventory would misrepresent the target by an order
//   of magnitude, so this screen always states the boundary and always
//   quantifies how much of the estate falls OUTSIDE it.
//
//   TAB 1  Import CSG — read the workbook, find the Compute and Storage Bill of
//          Materials sheets (there are title rows above the header row), map the
//          columns, and store the parsed line items under a label + environment
//          kind so several CSGs can coexist.
//
//   TAB 2  Current vs target (Epic only) — the consultant declares which current
//          servers are inside the Epic boundary, and the screen compares that
//          subset (and only that subset) with the CSG, per Epic Tier, ending in
//          the two lists that are the actual deliverable: current Epic servers
//          with no CSG line, and CSG lines with no current equivalent.
//
//   TAB 3  CSG versions — the CSG is a point-in-time draft and gets reissued, so
//          "what changed since the last one" is a recurring question. Diff any
//          two imports line by line.
//
// A deliberate discipline throughout: nothing is estimated. The workbook states
// cloud resource types and quantities; it does not state vCPU or RAM per type,
// so those cells read "—" with the reason attached rather than being filled in
// from outside knowledge.
//
// Persistence is localStorage under 'primordia.csg.<tenantId>' (the same
// convention as Saved Views). The CSG is a supplier document, not a discovered
// fact about the estate, so it deliberately does not create fact sheets that
// would then pollute the inventory counts.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, Grid,
  IconButton, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as XLSX from 'xlsx';
import { exportCsv } from './exports';
import store from './store';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const AMBER = '#B26A00';
const GREEN = '#2E7D32';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

// The tenant holds 7,000+ servers. Nothing paints more rows than this; every
// count, roll-up and export still covers the full matching set.
const ROW_CAP = 300;

const DASH = '—';
const ENV_KINDS = ['Production', 'Isolated Recovery Environment', 'Disaster Recovery', 'Non-Production'];

const norm = (s) => String(s === undefined || s === null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');
const lower = (s) => String(s === undefined || s === null ? '' : s).trim().toLowerCase();
const text = (v) => String(v === undefined || v === null ? '' : v).trim();

// A cell that is a number, optionally wearing a currency symbol, thousands
// separators, an exponent and/or a trailing unit ("$1,234.56", "1,234.56 GB",
// "85%", "1.2e3"). Anchored on purpose: a label such as "Windows Server 2019"
// or "prod-cluster-02" must come back as null, not as the digits buried in it.
const NUM_TEXT_RE = /^(?:[$€£¥]\s*)?(\d[\d,]*(?:\.\d+)?|\.\d+)(?:\s*[eE]([+-]?\d+))?\s*(?:%|[A-Za-z/µ]{1,8})?$/;

// Accounting notation: finance and vendor workbooks (the CSG included) write a
// negative as "(1,234.56)", not "-1,234.56". Stripping the parentheses without
// negating does not lose the sign, it INVERTS it — a credit becomes a charge,
// wrong by twice the value and in the direction that inflates what a client is
// shown. The parenthesis branch below is load-bearing; do not "simplify" it away.
export function parseNum(v) {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (!s) return null;
  let neg = false;
  const wrapped = /^\((.*)\)$/.exec(s);
  if (wrapped) { neg = true; s = wrapped[1].trim(); }
  if (s[0] === '+' || s[0] === '-') { if (s[0] === '-') neg = !neg; s = s.slice(1).trim(); }
  const m = NUM_TEXT_RE.exec(s);
  if (!m) return null;
  const n = parseFloat(m[1].replace(/,/g, '') + (m[2] ? `e${m[2]}` : ''));
  if (!Number.isFinite(n)) return null;
  if (n === 0) return 0;
  return neg ? -n : n;
}
const num = (v) => parseNum(v) || 0;
const fmt =(n) => (n === null || n === undefined || Number.isNaN(n) ? DASH : Number(n).toLocaleString());

// ---------------------------------------------------------------------------
// Workbook reading
// ---------------------------------------------------------------------------
// CSG sheets carry a title block ("Epic Cloud Specifications Guide", the client
// name, a revision date) above the real header row, so the header is found the
// same way the rest of the product finds it: the first row with the most
// non-empty cells. Duplicate header names are suffixed exactly the way SheetJS
// suffixes them ("Cloud Resource" / "Cloud Resource_1"), because the CSG puts a
// second Cloud Resource + Quantity pair on the row for the alternate sizing.
export function readSheetTable(wb, sheetName) {
  const ws = wb && wb.Sheets ? wb.Sheets[sheetName] : null;
  if (!ws) return { headers: [], rows: [], headerRow: 0 };
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  let hIdx = 0;
  let best = 0;
  for (let i = 0; i < Math.min(raw.length, 20); i += 1) {
    const c = (raw[i] || []).filter((x) => text(x)).length;
    if (c > best) { best = c; hIdx = i; }
  }
  const seen = new Map();
  const headers = (raw[hIdx] || [])
    .map((h) => text(h))
    .filter(Boolean)
    .map((h) => {
      const n = seen.get(h) || 0;
      seen.set(h, n + 1);
      return n ? `${h}_${n}` : h;
    });
  const rows = XLSX.utils
    .sheet_to_json(ws, { range: hIdx, defval: '' })
    .filter((r) => Object.values(r).some((v) => text(v)));
  return { headers, rows, headerRow: hIdx + 1 };
}

// Sheet names are the reliable signal — clients rename the file, never the tabs.
export function detectCsgSheets(sheetNames) {
  const names = sheetNames || [];
  const pick = (word) => names.find((n) => {
    const x = norm(n);
    return x.indexOf(word) >= 0 && (x.indexOf('billofmaterial') >= 0 || x.indexOf('bom') >= 0);
  }) || names.find((n) => norm(n).indexOf(word) >= 0) || '';
  return { compute: pick('compute'), storage: pick('storage') };
}

// ---------------------------------------------------------------------------
// Column mapping
// ---------------------------------------------------------------------------
// Two passes, exact alias then contains, so "Quantity" wins over "OS Disk
// Snapshot Quantity" and the alternate-sizing pair lands in its own targets.
const COMPUTE_TARGETS = [
  { key: 'epicTier', label: 'Epic Tier', aliases: ['epictier', 'tier', 'epictiername'] },
  { key: 'stamp', label: 'Stamp', aliases: ['stamp', 'stampname', 'epicstamp'] },
  { key: 'epicResource', label: 'Epic Resource', aliases: ['epicresource', 'epicrole', 'resourcename', 'epicresourcetype'] },
  { key: 'cloudResource', label: 'Cloud Resource', aliases: ['cloudresource', 'cloudresourcetype', 'instancetype', 'vmsize', 'cloudsku'] },
  { key: 'quantity', label: 'Quantity', aliases: ['quantity', 'qty', 'count', 'instancecount'] },
  { key: 'altCloudResource', label: 'Cloud Resource (alternate)', aliases: ['cloudresource1', 'cloudresource2', 'alternatecloudresource', 'altcloudresource', 'cloudresourcealternate', 'cloudresourceoption2'] },
  { key: 'altQuantity', label: 'Quantity (alternate)', aliases: ['quantity1', 'quantity2', 'alternatequantity', 'altquantity', 'quantityalternate'] },
  { key: 'osDiskType', label: 'OS Disk Type', aliases: ['osdisktype', 'osdiskstoragetype', 'disktype'] },
  { key: 'osDiskGb', label: 'OS Disk Capacity (GB)', aliases: ['osdiskcapacitygb', 'osdiskcapacity', 'osdiskgb', 'osdisksizegb', 'osdisksize'] },
  { key: 'osDiskSnapshots', label: 'OS Disk Snapshot Quantity', aliases: ['osdisksnapshotquantity', 'osdisksnapshots', 'snapshotquantity'] },
  // Only used if this particular workbook happens to state them. Never invented.
  { key: 'vcpuEach', label: 'vCPU per resource (only if stated)', aliases: ['vcpu', 'vcpus', 'vcpucount', 'cores', 'corecount', 'cpu'] },
  { key: 'ramGbEach', label: 'RAM GB per resource (only if stated)', aliases: ['memorygb', 'ramgb', 'memorygib', 'memory', 'ram'] },
];

const STORAGE_TARGETS = [
  { key: 'epicTier', label: 'Epic Tier', aliases: ['epictier', 'tier', 'epictiername'] },
  { key: 'stamp', label: 'Stamp', aliases: ['stamp', 'stampname', 'epicstamp'] },
  { key: 'epicResource', label: 'Epic Resource', aliases: ['epicresource', 'epicrole', 'resourcename', 'volumename', 'volume'] },
  { key: 'storageType', label: 'Storage / Cloud Resource', aliases: ['storagetype', 'cloudresource', 'disktype', 'volumetype', 'storageclass', 'cloudresourcetype'] },
  { key: 'capacityGb', label: 'Capacity (GB)', aliases: ['capacitygb', 'capacity', 'sizegb', 'diskcapacitygb', 'storagegb', 'datadiskcapacitygb', 'provisionedgb'] },
  { key: 'quantity', label: 'Quantity', aliases: ['quantity', 'qty', 'count', 'diskquantity'] },
  // PROVISIONED IOPS IS NOT OPTIONAL DETAIL. For an Epic build it is the single
  // most consequential storage figure: io1/io2 with provisioned IOPS and gp3 at
  // its baseline cost very different amounts and perform very differently, and
  // the operational database tier depends on the provisioned figure being met.
  // The column was in the workbook and simply was not mapped, so every imported
  // CSG silently lost it and the target-architecture view had to print "not
  // captured" against exactly the volumes where it matters most.
  { key: 'iops', label: 'IOPS', aliases: ['iops', 'provisionediops', 'iopsprovisioned', 'diskiops', 'baselineiops'] },
  { key: 'throughputMbs', label: 'Throughput (MB/s)', aliases: ['throughputmbs', 'throughput', 'throughputmbsec', 'mbps', 'throughputmib'] },
  { key: 'volumeDescription', label: 'Volume Description', aliases: ['volumedescription', 'volumedesc', 'description', 'volumelabel'] },
  { key: 'region', label: 'Region', aliases: ['region', 'cloudregion', 'awsregion', 'azureregion'] },
  { key: 'snapshotQuantity', label: 'Snapshot Quantity', aliases: ['snapshotquantity', 'snapshots', 'snapshotcount', 'snapshotqty'] },
];

export function autoMapColumns(headers, targets) {
  const map = {};
  const usedHeader = new Set();
  const usedTarget = new Set();

  targets.forEach((t) => {
    const hit = (headers || []).find((h) => !usedHeader.has(h) && t.aliases.indexOf(norm(h)) >= 0);
    if (hit) { map[hit] = t.key; usedHeader.add(hit); usedTarget.add(t.key); }
  });

  targets.forEach((t) => {
    if (usedTarget.has(t.key)) return;
    const hit = (headers || []).find((h) => {
      if (usedHeader.has(h)) return false;
      const n = norm(h);
      return t.aliases.some((a) => a.length >= 5 && n.indexOf(a) >= 0);
    });
    if (hit) { map[hit] = t.key; usedHeader.add(hit); usedTarget.add(t.key); }
  });

  (headers || []).forEach((h) => { if (!map[h]) map[h] = ''; });
  return map;
}

const NUMERIC_KEYS = new Set([
  'quantity', 'altQuantity', 'osDiskGb', 'osDiskSnapshots', 'vcpuEach', 'ramGbEach', 'capacityGb',
  // Kept numeric so totals and the io2-vs-gp3 comparison are arithmetic rather
  // than string handling. A cell reading "N/A" parses to null and stays null —
  // never zero, because a volume with no stated IOPS is unknown, not unprovisioned.
  'iops', 'throughputMbs', 'snapshotQuantity',
]);

// A row only becomes a line item if it carries something identifying — CSG
// sheets are full of spacer rows, sub-totals and footnotes.
export function buildLines(rows, map, kind) {
  const out = [];
  const pairs = Object.keys(map || {}).filter((h) => map[h]);
  (rows || []).forEach((row, i) => {
    const line = { id: `${kind}-${i}` };
    pairs.forEach((h) => {
      const key = map[h];
      const raw = row[h];
      if (NUMERIC_KEYS.has(key)) {
        const n = parseNum(raw);
        line[key] = n === null ? null : n;
      } else {
        line[key] = text(raw);
      }
    });
    const identified = text(line.epicTier) || text(line.epicResource)
      || text(line.cloudResource) || text(line.storageType);
    if (!identified) return;
    // Drop the sub-total / grand-total rows the CSG puts under each block: they
    // carry a quantity but no cloud resource, and counting them would double the
    // target instance count.
    const heading = `${line.epicTier || ''} ${line.stamp || ''} ${line.epicResource || ''}`;
    const named = text(line.cloudResource) || text(line.storageType);
    if (!named && /(^|[^a-z])((grand|sub)\s*)?totals?([^a-z]|$)/i.test(heading)) return;
    if (!named && /^note[s]?\b|^\*/i.test(heading.trim())) return;
    out.push(line);
  });
  return out;
}

// ---------------------------------------------------------------------------
// Epic scope + tier matching
// ---------------------------------------------------------------------------
// Every heuristic here is correctable by the consultant in the UI, and every
// derived number says which rule produced it. Nothing about Epic sizing is
// assumed; these only decide WHICH ROWS ARE COMPARED, never what they cost.
const RESOURCE_NOISE = new Set([
  'server', 'servers', 'node', 'nodes', 'instance', 'instances', 'virtual',
  'machine', 'machines', 'host', 'hosts', 'total', 'count', 'the', 'and', 'for',
  'with', 'vms', 'pool', 'tier', 'stamp', 'epic',
]);

export function tokensOf(s, minLen = 4) {
  return String(s || '')
    .split(/[^A-Za-z0-9]+/)
    .map((w) => lower(w))
    .filter((w) => w.length >= minLen && !RESOURCE_NOISE.has(w));
}

// The haystack a server is matched against: its name plus the operational
// context that hospitals actually encode Epic into (folder, cluster, DNS).
export function serverText(s) {
  const f = s.fields || {};
  return norm([s.name, f.dnsName, f.folder, f.cluster, f.datacenter, f.annotation].filter(Boolean).join(' '));
}

export function matchesPattern(s, patternTokens) {
  if (!patternTokens.length) return false;
  const hay = serverText(s);
  return patternTokens.some((t) => t && hay.indexOf(t) >= 0);
}

// Which servers the consultant has declared to be inside the Epic boundary.
export function computeScope(servers, scope) {
  const patternTokens = String(scope.pattern || '')
    .split(',')
    .map((p) => norm(p))
    .filter(Boolean);
  const clusters = new Set((scope.clusters || []).map(lower));
  const folders = new Set((scope.folders || []).map(lower));
  const include = new Set(scope.include || []);
  const exclude = new Set(scope.exclude || []);

  const inScope = [];
  const outScope = [];
  servers.forEach((s) => {
    const f = s.fields || {};
    let why = '';
    if (include.has(s.id)) why = 'Manually included';
    else if (clusters.size && clusters.has(lower(f.cluster))) why = `Cluster "${f.cluster}"`;
    else if (folders.size && folders.has(lower(f.folder))) why = `Folder "${f.folder}"`;
    else if (matchesPattern(s, patternTokens)) why = 'Name / folder pattern';
    if (why && !exclude.has(s.id)) inScope.push({ ...s, __why: why });
    else outScope.push(s);
  });
  return { inScope, outScope };
}

export function estateTotals(list) {
  let vcpu = 0;
  let ram = 0;
  let storage = 0;
  let missingVcpu = 0;
  let missingRam = 0;
  let missingStorage = 0;
  list.forEach((s) => {
    const f = s.fields || {};
    const c = parseNum(f.vcpu);
    const r = parseNum(f.ramGb);
    const d = parseNum(f.storageGb);
    if (c === null) missingVcpu += 1; else vcpu += c;
    if (r === null) missingRam += 1; else ram += r;
    if (d === null) missingStorage += 1; else storage += d;
  });
  return {
    count: list.length,
    vcpu: Math.round(vcpu),
    ram: Math.round(ram),
    storage: Math.round(storage),
    missingVcpu,
    missingRam,
    missingStorage,
  };
}

export function csgTotals(csg) {
  const lines = (csg && csg.lines) || [];
  const storageLines = (csg && csg.storageLines) || [];
  let instances = 0;
  let altInstances = 0;
  let osDiskGb = 0;
  let osDiskStated = 0;
  let vcpu = 0;
  let vcpuStated = 0;
  let ram = 0;
  let ramStated = 0;
  lines.forEach((l) => {
    const q = num(l.quantity);
    instances += q;
    altInstances += num(l.altQuantity);
    if (l.osDiskGb !== null && l.osDiskGb !== undefined) { osDiskGb += l.osDiskGb * (q || 1); osDiskStated += 1; }
    if (l.vcpuEach !== null && l.vcpuEach !== undefined) { vcpu += l.vcpuEach * (q || 1); vcpuStated += 1; }
    if (l.ramGbEach !== null && l.ramGbEach !== undefined) { ram += l.ramGbEach * (q || 1); ramStated += 1; }
  });
  let storageGb = 0;
  let storageStated = 0;
  storageLines.forEach((l) => {
    if (l.capacityGb !== null && l.capacityGb !== undefined) {
      storageGb += l.capacityGb * (num(l.quantity) || 1);
      storageStated += 1;
    }
  });
  const distinct = new Set(lines.map((l) => lower(l.cloudResource)).filter(Boolean));
  return {
    lineCount: lines.length,
    storageLineCount: storageLines.length,
    instances,
    altInstances,
    distinctResources: distinct.size,
    // Stated-or-nothing: a total is only shown when the workbook supplied the column.
    osDiskGb: osDiskStated ? Math.round(osDiskGb) : null,
    vcpu: vcpuStated ? Math.round(vcpu) : null,
    ram: ramStated ? Math.round(ram) : null,
    storageGb: storageStated ? Math.round(storageGb) : null,
  };
}

// Server <-> CSG line matching. A line's identity for matching purposes is its
// Epic Resource wording plus its tier; a server matches when its name/folder
// carries one of those words and (if tier tokens exist) the tier word too.
export function matchServersToLines(inScope, lines, tierRules) {
  const prepared = lines.map((l) => {
    const tier = text(l.epicTier);
    const ruleRaw = tierRules && tierRules[tier] !== undefined ? tierRules[tier] : tier;
    const tierTokens = String(ruleRaw || '')
      .split(',')
      .map((t) => norm(t))
      .filter((t) => t.length >= 3);
    return {
      line: l,
      tier,
      tierTokens,
      resTokens: tokensOf(l.epicResource).map(norm).filter((t) => t.length >= 4),
    };
  });

  const serverToLine = new Map();
  const lineHits = new Map();

  inScope.forEach((s) => {
    const hay = serverText(s);
    let best = null;
    let bestScore = 0;
    prepared.forEach((p) => {
      const tierHit = !p.tierTokens.length ? 0 : (p.tierTokens.some((t) => hay.indexOf(t) >= 0) ? 1 : -1);
      if (tierHit < 0) return;
      const resHit = p.resTokens.filter((t) => hay.indexOf(t) >= 0);
      // A line with distinguishing wording has to have that wording matched —
      // a tier alone is not evidence that THIS line is the server's target.
      // A line whose wording is entirely generic falls back to the tier.
      if (p.resTokens.length && !resHit.length) return;
      if (!p.resTokens.length && tierHit <= 0) return;
      // Longer token matches are stronger evidence than more, shorter ones.
      const score = tierHit * 2 + resHit.reduce((a, t) => a + t.length, 0) / 4;
      if (score > bestScore) { bestScore = score; best = p; }
    });
    if (best) {
      serverToLine.set(s.id, { lineId: best.line.id, tier: best.tier, score: bestScore });
      lineHits.set(best.line.id, (lineHits.get(best.line.id) || 0) + 1);
    }
  });

  // Servers that matched no line still get a tier if a tier token appears.
  const tierOnly = new Map();
  const tierTokenSets = [];
  const seenTier = new Set();
  prepared.forEach((p) => {
    if (!p.tier || seenTier.has(p.tier)) return;
    seenTier.add(p.tier);
    tierTokenSets.push({ tier: p.tier, tokens: p.tierTokens });
  });
  inScope.forEach((s) => {
    if (serverToLine.has(s.id)) return;
    const hay = serverText(s);
    const hit = tierTokenSets.find((t) => t.tokens.some((x) => hay.indexOf(x) >= 0));
    if (hit) tierOnly.set(s.id, hit.tier);
  });

  return { serverToLine, lineHits, tierOnly };
}

export function gapStatement(row) {
  const t = row.tier || 'Untiered';
  if (!row.targetQty && row.currentCount) {
    return `${row.currentCount} current server(s) sit under "${t}" but the CSG has no line for it — either they are outside the Epic footprint or this CSG draft omits them.`;
  }
  if (row.targetQty && !row.currentCount) {
    return `The CSG asks for ${row.targetQty} instance(s) under "${t}" with nothing matched in the current estate — treat as new build.`;
  }
  const d = row.targetQty - row.currentCount;
  if (d === 0) {
    return `Counts line up (${row.currentCount} current, ${row.targetQty} target). The workbook does not state vCPU/RAM per cloud resource, so this is a count comparison only.`;
  }
  if (d > 0) {
    return `${d} more target instance(s) than current servers (${row.targetQty} vs ${row.currentCount}) — new capacity in the CSG.`;
  }
  return `${-d} fewer target instance(s) than current servers (${row.targetQty} vs ${row.currentCount}) — consolidation, or current servers that are not really Epic.`;
}

// ---------------------------------------------------------------------------
// Version diff
// ---------------------------------------------------------------------------
const diffKey = (l) => [l.epicTier, l.stamp, l.epicResource, l.cloudResource].map((x) => lower(x)).join(' | ');

export function diffCsg(prev, next) {
  const fold = (csg) => {
    const m = new Map();
    ((csg && csg.lines) || []).forEach((l) => {
      const k = diffKey(l);
      const cur = m.get(k);
      if (cur) { cur.quantity += num(l.quantity); cur.osDiskGb = cur.osDiskGb === null ? l.osDiskGb : cur.osDiskGb; }
      else {
        m.set(k, {
          key: k,
          epicTier: text(l.epicTier),
          stamp: text(l.stamp),
          epicResource: text(l.epicResource),
          cloudResource: text(l.cloudResource),
          osDiskGb: l.osDiskGb === undefined ? null : l.osDiskGb,
          quantity: num(l.quantity),
        });
      }
    });
    return m;
  };
  const a = fold(prev);
  const b = fold(next);
  const keys = [...new Set([...a.keys(), ...b.keys()])].sort();
  const rows = keys.map((k) => {
    const from = a.get(k);
    const to = b.get(k);
    const src = to || from;
    if (!from) return { ...src, status: 'Added', prevQty: null, nextQty: to.quantity, delta: to.quantity };
    if (!to) return { ...src, status: 'Removed', prevQty: from.quantity, nextQty: null, delta: -from.quantity };
    const delta = to.quantity - from.quantity;
    return {
      ...src,
      status: delta === 0 ? 'Unchanged' : 'Changed',
      prevQty: from.quantity,
      nextQty: to.quantity,
      delta,
    };
  });
  const changed = rows.filter((r) => r.status !== 'Unchanged');
  return {
    rows,
    changed,
    added: rows.filter((r) => r.status === 'Added').length,
    removed: rows.filter((r) => r.status === 'Removed').length,
    modified: rows.filter((r) => r.status === 'Changed').length,
    unchanged: rows.length - changed.length,
  };
}

// ---------------------------------------------------------------------------
// Persistence — one localStorage key holds the CSGs and the scoping decisions
// ---------------------------------------------------------------------------
const EMPTY_DB = { version: 1, csgs: [], scope: null, tierRules: {} };
const DEFAULT_SCOPE = {
  pattern: 'epic, cch, hyperspace, cache, clarity',
  clusters: [],
  folders: [],
  include: [],
  exclude: [],
};

export function readDb(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { ...EMPTY_DB, scope: { ...DEFAULT_SCOPE } };
    const parsed = JSON.parse(raw);
    return {
      version: 1,
      csgs: Array.isArray(parsed.csgs) ? parsed.csgs : [],
      scope: { ...DEFAULT_SCOPE, ...(parsed.scope || {}) },
      tierRules: parsed.tierRules || {},
    };
  } catch (e) {
    return { ...EMPTY_DB, scope: { ...DEFAULT_SCOPE } };
  }
}

function writeDb(key, db) {
  try { localStorage.setItem(key, JSON.stringify(db)); } catch (e) { /* quota — state still holds it */ }
}

const newId = () => `csg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------
function Kpi({ label, value, sub, color = NAVY }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" sx={{ color: GREY }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
      {sub ? <Typography variant="body2" sx={{ color: GREY }}>{sub}</Typography> : null}
    </Paper>
  );
}

function SectionTitle({ children, sx }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, ...(sx || {}) }}>
      {children}
    </Typography>
  );
}

// The disclaimer that has to be on screen no matter which tab is open.
function EpicOnlyBanner({ outOfScope, total }) {
  return (
    <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
      <b>The Epic Cloud Specifications Guide covers the Epic footprint only — not the whole estate.</b>{' '}
      It is a target-state bill of materials for Epic. CSG totals must never be read as a target for
      every server in this workspace.
      {total ? (
        <>
          {' '}This workspace holds <b>{fmt(total)}</b> servers, of which{' '}
          <b>{fmt(outOfScope)}</b> fall outside the Epic scope defined on the comparison tab and are
          therefore <b>not covered by any CSG line</b>.
        </>
      ) : null}
    </Alert>
  );
}

function EmptyState({ icon, title, body, action }) {
  return (
    <Paper sx={{ p: 5, textAlign: 'center', border: '2px dashed #cfd8dc', boxShadow: 'none' }}>
      {icon}
      <Typography sx={{ fontWeight: 700, mt: 1, color: NAVY }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: GREY, maxWidth: 720, mx: 'auto', mt: 0.5 }}>{body}</Typography>
      {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
    </Paper>
  );
}

function CapNote({ shown, total, what }) {
  if (total <= shown) return null;
  return (
    <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 1 }}>
      Showing the first {fmt(shown)} of {fmt(total)} {what} — every count and export above covers all {fmt(total)}.
    </Typography>
  );
}

// ===========================================================================
// Component
// ===========================================================================
export default function CsgTargetState({ tenantId }) {
  const [tab, setTab] = useState(0);
  const lsKey = `primordia.csg.${tenantId}`;

  const [db, setDb] = useState(() => ({ ...EMPTY_DB, scope: { ...DEFAULT_SCOPE } }));
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  // ---- Tab 1 (import) state ----------------------------------------------
  const fileRef = useRef(null);
  const [wb, setWb] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [fileName, setFileName] = useState('');
  const [computeSheet, setComputeSheet] = useState('');
  const [storageSheet, setStorageSheet] = useState('');
  const [computeTable, setComputeTable] = useState({ headers: [], rows: [], headerRow: 0 });
  const [storageTable, setStorageTable] = useState({ headers: [], rows: [], headerRow: 0 });
  const [computeMap, setComputeMap] = useState({});
  const [storageMap, setStorageMap] = useState({});
  const [label, setLabel] = useState('');
  const [envKind, setEnvKind] = useState(ENV_KINDS[0]);
  const [importErr, setImportErr] = useState('');
  const [importMsg, setImportMsg] = useState('');

  // ---- Tab 2 / 3 selection state -----------------------------------------
  const [activeCsgId, setActiveCsgId] = useState('');
  const [scopeSearch, setScopeSearch] = useState('');
  const [leftId, setLeftId] = useState('');
  const [rightId, setRightId] = useState('');
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => { setDb(readDb(lsKey)); }, [lsKey]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadErr('');
    store.listFactSheets(tenantId, { type: 'ITComponent' })
      .then((rows) => { if (live) setServers(rows || []); })
      .catch(() => { if (live) setLoadErr('Could not load the servers for this workspace. Reload the page and try again.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [tenantId]);

  const persist = (next) => { setDb(next); writeDb(lsKey, next); };

  const csgs = useMemo(() => db.csgs || [], [db.csgs]);
  const scope = useMemo(() => db.scope || DEFAULT_SCOPE, [db.scope]);

  // Default selections follow the newest import.
  useEffect(() => {
    if (!csgs.length) { setActiveCsgId(''); setLeftId(''); setRightId(''); return; }
    setActiveCsgId((cur) => (csgs.some((c) => c.id === cur) ? cur : csgs[0].id));
    setRightId((cur) => (csgs.some((c) => c.id === cur) ? cur : csgs[0].id));
    setLeftId((cur) => (csgs.some((c) => c.id === cur) ? cur : (csgs[1] ? csgs[1].id : csgs[0].id)));
  }, [csgs]);

  const activeCsg = useMemo(
    () => csgs.find((c) => c.id === activeCsgId) || csgs[0] || null,
    [csgs, activeCsgId]
  );

  // =========================================================================
  // TAB 1 — import
  // =========================================================================
  const loadSheets = (book, compute, storage) => {
    const c = compute ? readSheetTable(book, compute) : { headers: [], rows: [], headerRow: 0 };
    const s = storage ? readSheetTable(book, storage) : { headers: [], rows: [], headerRow: 0 };
    setComputeTable(c);
    setStorageTable(s);
    setComputeMap(autoMapColumns(c.headers, COMPUTE_TARGETS));
    setStorageMap(autoMapColumns(s.headers, STORAGE_TARGETS));
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportErr('');
    setImportMsg('');
    try {
      const buf = await file.arrayBuffer();
      const book = XLSX.read(buf, { type: 'array' });
      const det = detectCsgSheets(book.SheetNames);
      setWb(book);
      setFileName(file.name);
      setSheetNames(book.SheetNames);
      const compute = det.compute || book.SheetNames[0] || '';
      const storage = det.storage || '';
      setComputeSheet(compute);
      setStorageSheet(storage);
      loadSheets(book, compute, storage);
      if (!label) {
        const guessAws = /aws/i.test(file.name);
        const guessIre = /(ire|isolated|recovery)/i.test(file.name);
        setLabel(`${guessAws ? 'AWS ' : ''}CSG ${new Date().toISOString().slice(0, 10)}`);
        if (guessIre) setEnvKind('Isolated Recovery Environment');
      }
      if (!det.compute) {
        setImportErr('No sheet obviously named "Compute Bill of Materials" — pick the right sheet below.');
      }
    } catch (err) {
      setImportErr('Could not read that file. Supported: .xlsx, .xls, .csv');
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const resetFile = () => {
    setWb(null); setSheetNames([]); setFileName(''); setComputeSheet(''); setStorageSheet('');
    setComputeTable({ headers: [], rows: [], headerRow: 0 });
    setStorageTable({ headers: [], rows: [], headerRow: 0 });
    setComputeMap({}); setStorageMap({}); setImportErr('');
  };

  const parsedCompute = useMemo(() => buildLines(computeTable.rows, computeMap, 'c'), [computeTable, computeMap]);
  const parsedStorage = useMemo(() => buildLines(storageTable.rows, storageMap, 's'), [storageTable, storageMap]);

  const computeMapped = useMemo(() => new Set(Object.values(computeMap).filter(Boolean)), [computeMap]);
  const missingCore = ['epicTier', 'epicResource', 'cloudResource', 'quantity'].filter((k) => !computeMapped.has(k));

  const saveCsg = () => {
    if (!parsedCompute.length) { setImportErr('Nothing to save — no Compute Bill of Materials rows were parsed.'); return; }
    if (!label.trim()) { setImportErr('Give this CSG a label (e.g. "AWS CSG 2026-04-09") so versions can be told apart.'); return; }
    const record = {
      id: newId(),
      tenantId,
      label: label.trim(),
      envKind,
      fileName,
      computeSheet,
      storageSheet,
      importedAt: new Date().toISOString(),
      lines: parsedCompute,
      storageLines: parsedStorage,
      columnsMapped: [...computeMapped],
    };
    const next = { ...db, csgs: [record, ...csgs] };
    persist(next);
    setActiveCsgId(record.id);
    setImportMsg(`Saved "${record.label}" — ${record.lines.length} compute line(s), ${record.storageLines.length} storage line(s).`);
    setImportErr('');
    resetFile();
  };

  const deleteCsg = (id) => {
    persist({ ...db, csgs: csgs.filter((c) => c.id !== id) });
  };

  const exportParsed = () => {
    exportCsv(`csg-parsed-${norm(label) || 'draft'}.csv`, parsedCompute, [
      { label: 'Epic Tier', value: 'epicTier' },
      { label: 'Stamp', value: 'stamp' },
      { label: 'Epic Resource', value: 'epicResource' },
      { label: 'Cloud Resource', value: 'cloudResource' },
      { label: 'Quantity', value: (r) => (r.quantity === null || r.quantity === undefined ? '' : r.quantity) },
      { label: 'Cloud Resource (alt)', value: 'altCloudResource' },
      { label: 'Quantity (alt)', value: (r) => (r.altQuantity === null || r.altQuantity === undefined ? '' : r.altQuantity) },
      { label: 'OS Disk Type', value: 'osDiskType' },
      { label: 'OS Disk Capacity (GB)', value: (r) => (r.osDiskGb === null || r.osDiskGb === undefined ? '' : r.osDiskGb) },
      { label: 'OS Disk Snapshot Quantity', value: (r) => (r.osDiskSnapshots === null || r.osDiskSnapshots === undefined ? '' : r.osDiskSnapshots) },
    ]);
    // The Storage bill of materials was parsed but never exported, so the
    // provisioned IOPS figure — the one that decides io2 versus gp3, and the one
    // an Epic operational database is sized against — could not leave the
    // screen. Emitted as its own file because the two bills have different
    // shapes and forcing them into one table loses both.
    if (parsedStorage.length) {
      const orDash = (v) => (v === null || v === undefined || v === '' ? '' : v);
      exportCsv(`csg-parsed-storage-${norm(label) || 'draft'}.csv`, parsedStorage, [
        { label: 'Epic Tier', value: 'epicTier' },
        { label: 'Stamp', value: 'stamp' },
        { label: 'Epic Resource', value: 'epicResource' },
        { label: 'Region', value: (r) => orDash(r.region) },
        { label: 'Volume Description', value: (r) => orDash(r.volumeDescription) },
        { label: 'Disk Type', value: 'storageType' },
        { label: 'Quantity', value: (r) => orDash(r.quantity) },
        { label: 'IOPS', value: (r) => orDash(r.iops) },
        { label: 'Throughput (MB/s)', value: (r) => orDash(r.throughputMbs) },
        { label: 'Capacity (GB)', value: (r) => orDash(r.capacityGb) },
        { label: 'Snapshot Quantity', value: (r) => orDash(r.snapshotQuantity) },
      ]);
    }
  };

  // =========================================================================
  // TAB 2 — current vs target (Epic only)
  // =========================================================================
  const clusterOptions = useMemo(() => {
    const set = new Set();
    servers.forEach((s) => { const c = text((s.fields || {}).cluster); if (c) set.add(c); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [servers]);

  const folderOptions = useMemo(() => {
    const set = new Set();
    servers.forEach((s) => { const f = text((s.fields || {}).folder); if (f) set.add(f); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [servers]);

  const { inScope, outScope } = useMemo(() => computeScope(servers, scope), [servers, scope]);

  const currentTotals = useMemo(() => estateTotals(inScope), [inScope]);
  const outTotals = useMemo(() => estateTotals(outScope), [outScope]);
  const targetTotals = useMemo(() => csgTotals(activeCsg), [activeCsg]);

  const activeLines = useMemo(() => (activeCsg && activeCsg.lines) || [], [activeCsg]);

  const matching = useMemo(
    () => matchServersToLines(inScope, activeLines, db.tierRules || {}),
    [inScope, activeLines, db.tierRules]
  );

  // Per-Epic-Tier roll-up: current servers on the left, CSG resources on the
  // right, delta and a plain-English sentence for the report.
  const tierRows = useMemo(() => {
    const map = new Map();
    const touch = (tier) => {
      const k = tier || DASH;
      if (!map.has(k)) {
        map.set(k, {
          tier: k, currentCount: 0, currentVcpu: 0, currentRam: 0, currentStorage: 0,
          targetQty: 0, targetLines: 0, resources: new Set(), targetVcpu: null, targetRam: null,
        });
      }
      return map.get(k);
    };

    activeLines.forEach((l) => {
      const row = touch(text(l.epicTier));
      row.targetQty += num(l.quantity);
      row.targetLines += 1;
      if (text(l.cloudResource)) row.resources.add(text(l.cloudResource));
      if (l.vcpuEach !== null && l.vcpuEach !== undefined) {
        row.targetVcpu = (row.targetVcpu || 0) + l.vcpuEach * (num(l.quantity) || 1);
      }
      if (l.ramGbEach !== null && l.ramGbEach !== undefined) {
        row.targetRam = (row.targetRam || 0) + l.ramGbEach * (num(l.quantity) || 1);
      }
    });

    inScope.forEach((s) => {
      const hit = matching.serverToLine.get(s.id);
      const tier = hit ? hit.tier : (matching.tierOnly.get(s.id) || '');
      const row = touch(tier || 'Unmatched (in Epic scope)');
      const f = s.fields || {};
      row.currentCount += 1;
      row.currentVcpu += num(f.vcpu);
      row.currentRam += num(f.ramGb);
      row.currentStorage += num(f.storageGb);
    });

    return [...map.values()]
      .map((r) => ({
        ...r,
        resourceList: [...r.resources].sort((a, b) => a.localeCompare(b)),
        currentRam: Math.round(r.currentRam),
        currentStorage: Math.round(r.currentStorage),
        delta: r.targetQty - r.currentCount,
      }))
      .map((r) => ({ ...r, gap: gapStatement(r) }))
      .sort((a, b) => (b.targetQty + b.currentCount) - (a.targetQty + a.currentCount) || a.tier.localeCompare(b.tier));
  }, [activeLines, inScope, matching]);

  // FINDING 1 — current Epic servers the CSG does not account for.
  const unmatchedServers = useMemo(
    () => inScope
      .filter((s) => !matching.serverToLine.has(s.id))
      .map((s) => ({
        ...s,
        __tier: matching.tierOnly.get(s.id) || '',
        __reason: matching.tierOnly.get(s.id)
          ? `Tier "${matching.tierOnly.get(s.id)}" recognised, but no Epic Resource line matched the hostname`
          : 'No Epic Tier or Epic Resource wording matched this hostname / folder',
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [inScope, matching]
  );

  // FINDING 2 — CSG lines with nothing equivalent today (new build).
  const newBuildLines = useMemo(
    () => activeLines
      .filter((l) => !matching.lineHits.get(l.id))
      .sort((a, b) => (num(b.quantity) - num(a.quantity))
        || String(a.epicTier).localeCompare(String(b.epicTier))),
    [activeLines, matching]
  );

  const matchedLines = useMemo(
    () => activeLines.filter((l) => matching.lineHits.get(l.id)).length,
    [activeLines, matching]
  );

  const scopePreview = useMemo(() => {
    const q = lower(scopeSearch);
    const list = q ? inScope.filter((s) => lower(s.name).indexOf(q) >= 0) : inScope;
    return { list, total: list.length };
  }, [inScope, scopeSearch]);

  const setScope = (patch) => persist({ ...db, scope: { ...scope, ...patch } });
  const setTierRule = (tier, value) => persist({ ...db, tierRules: { ...(db.tierRules || {}), [tier]: value } });

  const exportTiers = () => {
    exportCsv(`epic-tier-current-vs-target-${norm(tenantId)}.csv`, tierRows, [
      { label: 'Epic Tier', value: 'tier' },
      { label: 'Current servers (Epic scope)', value: 'currentCount' },
      { label: 'Current vCPU', value: 'currentVcpu' },
      { label: 'Current RAM (GB)', value: 'currentRam' },
      { label: 'Current storage (GB)', value: 'currentStorage' },
      { label: 'CSG target instances', value: 'targetQty' },
      { label: 'CSG cloud resources', value: (r) => r.resourceList.join(' | ') },
      { label: 'CSG vCPU (only if stated)', value: (r) => (r.targetVcpu === null ? '' : Math.round(r.targetVcpu)) },
      { label: 'Delta (target - current)', value: 'delta' },
      { label: 'Gap statement', value: 'gap' },
    ]);
  };

  const exportUnmatched = () => {
    exportCsv(`epic-servers-with-no-csg-line-${norm(tenantId)}.csv`, unmatchedServers, [
      { label: 'Server', value: 'name' },
      { label: 'In-scope because', value: '__why' },
      { label: 'Tier guess', value: '__tier' },
      { label: 'Why no CSG line', value: '__reason' },
      { label: 'OS', value: (s) => (s.fields || {}).os || '' },
      { label: 'vCPU', value: (s) => (s.fields || {}).vcpu || '' },
      { label: 'RAM (GB)', value: (s) => (s.fields || {}).ramGb || '' },
      { label: 'Storage (GB)', value: (s) => (s.fields || {}).storageGb || '' },
      { label: 'Cluster', value: (s) => (s.fields || {}).cluster || '' },
      { label: 'Folder', value: (s) => (s.fields || {}).folder || '' },
      { label: 'Power state', value: (s) => (s.fields || {}).powerState || '' },
    ]);
  };

  const exportNewBuild = () => {
    exportCsv(`csg-lines-with-no-current-equivalent-${norm(tenantId)}.csv`, newBuildLines, [
      { label: 'Epic Tier', value: 'epicTier' },
      { label: 'Stamp', value: 'stamp' },
      { label: 'Epic Resource', value: 'epicResource' },
      { label: 'Cloud Resource', value: 'cloudResource' },
      { label: 'Quantity', value: (l) => (l.quantity === null || l.quantity === undefined ? '' : l.quantity) },
      { label: 'OS Disk Type', value: 'osDiskType' },
      { label: 'OS Disk Capacity (GB)', value: (l) => (l.osDiskGb === null || l.osDiskGb === undefined ? '' : l.osDiskGb) },
    ]);
  };

  const exportOutOfScope = () => {
    exportCsv(`servers-outside-epic-csg-scope-${norm(tenantId)}.csv`, outScope, [
      { label: 'Server', value: 'name' },
      { label: 'OS', value: (s) => (s.fields || {}).os || '' },
      { label: 'vCPU', value: (s) => (s.fields || {}).vcpu || '' },
      { label: 'RAM (GB)', value: (s) => (s.fields || {}).ramGb || '' },
      { label: 'Storage (GB)', value: (s) => (s.fields || {}).storageGb || '' },
      { label: 'Cluster', value: (s) => (s.fields || {}).cluster || '' },
      { label: 'Folder', value: (s) => (s.fields || {}).folder || '' },
      { label: 'Source vCenter', value: (s) => (s.fields || {}).sourceVcenter || '' },
    ]);
  };

  // =========================================================================
  // TAB 3 — versions
  // =========================================================================
  const leftCsg = useMemo(() => csgs.find((c) => c.id === leftId) || null, [csgs, leftId]);
  const rightCsg = useMemo(() => csgs.find((c) => c.id === rightId) || null, [csgs, rightId]);

  const diff = useMemo(
    () => (leftCsg && rightCsg && leftCsg.id !== rightCsg.id ? diffCsg(leftCsg, rightCsg) : null),
    [leftCsg, rightCsg]
  );

  const diffRows = useMemo(() => {
    if (!diff) return [];
    return showUnchanged ? diff.rows : diff.changed;
  }, [diff, showUnchanged]);

  const exportDiff = () => {
    if (!diff) return;
    exportCsv(`csg-diff-${norm(leftCsg.label)}-vs-${norm(rightCsg.label)}.csv`, diffRows, [
      { label: 'Status', value: 'status' },
      { label: 'Epic Tier', value: 'epicTier' },
      { label: 'Stamp', value: 'stamp' },
      { label: 'Epic Resource', value: 'epicResource' },
      { label: 'Cloud Resource', value: 'cloudResource' },
      { label: `${leftCsg.label} qty`, value: (r) => (r.prevQty === null ? '' : r.prevQty) },
      { label: `${rightCsg.label} qty`, value: (r) => (r.nextQty === null ? '' : r.nextQty) },
      { label: 'Delta', value: 'delta' },
    ]);
  };

  // =========================================================================
  // Render
  // =========================================================================
  const csgLabel = (c) => `${c.label} · ${c.envKind} · ${String(c.importedAt).slice(0, 10)}`;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Epic CSG target state</Typography>
        <Chip size="small" variant="outlined" label={`workspace: ${tenantId}`} />
        {loading ? <CircularProgress size={18} /> : (
          <Chip
            size="small"
            sx={{ bgcolor: '#eef3f9', color: NAVY, fontWeight: 600 }}
            label={`${fmt(servers.length)} servers imported · ${csgs.length} CSG version(s)`}
          />
        )}
      </Stack>

      {loadErr ? <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert> : null}

      <EpicOnlyBanner outOfScope={outScope.length} total={servers.length} />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label="Import CSG" sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label="Current vs target (Epic only)" sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label="CSG versions" sx={{ fontWeight: 700, textTransform: 'none' }} />
      </Tabs>

      {/* ============================ TAB 1 ============================ */}
      {tab === 0 ? (
        <Box>
          {importErr ? <Alert severity="warning" sx={{ mb: 2 }}>{importErr}</Alert> : null}
          {importMsg ? <Alert severity="success" sx={{ mb: 2 }}>{importMsg}</Alert> : null}

          {!wb ? (
            <EmptyState
              icon={<UploadFileIcon sx={{ fontSize: 44, color: BLUE }} />}
              title="Upload an Epic Cloud Specifications Guide workbook"
              body={'.xlsx or .xls — the AWS CSG or the Isolated Recovery Environment CSG. The sheets expected are '
                + 'Assumptions, Scope, Compute Bill of Materials, Storage Bill of Materials and Appendix; only the two '
                + 'Bill of Materials sheets are read. Give each upload a label and an environment kind so a reissued '
                + 'CSG can be compared against this one later.'}
              action={(
                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileRef.current && fileRef.current.click()}
                  sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                >
                  Choose CSG workbook
                </Button>
              )}
            />
          ) : (
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <SectionTitle sx={{ mb: 1.5 }}>This CSG</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth size="small" label="CSG label" value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      helperText='e.g. "AWS CSG 2026-04-09"'
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select fullWidth size="small" label="Environment kind" value={envKind}
                      onChange={(e) => setEnvKind(e.target.value)}
                      helperText="Keeps the AWS and Isolated Recovery CSGs apart"
                    >
                      {ENV_KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Chip label={fileName} onDelete={resetFile} />
                      <Chip variant="outlined" label={`${sheetNames.length} sheet(s)`} />
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 2, mb: 2 }}>
                <SectionTitle sx={{ mb: 1.5 }}>Sheets</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select fullWidth size="small" label="Compute Bill of Materials sheet" value={computeSheet}
                      onChange={(e) => { setComputeSheet(e.target.value); loadSheets(wb, e.target.value, storageSheet); }}
                      helperText={computeTable.headers.length
                        ? `Header row ${computeTable.headerRow} · ${computeTable.headers.length} columns · ${computeTable.rows.length} data rows`
                        : 'Pick the sheet that holds the compute bill of materials'}
                    >
                      {sheetNames.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select fullWidth size="small" label="Storage Bill of Materials sheet (optional)" value={storageSheet}
                      onChange={(e) => { setStorageSheet(e.target.value); loadSheets(wb, computeSheet, e.target.value); }}
                      helperText={storageTable.headers.length
                        ? `Header row ${storageTable.headerRow} · ${storageTable.headers.length} columns · ${storageTable.rows.length} data rows`
                        : 'Leave empty if this workbook has no storage bill of materials'}
                    >
                      <MenuItem value=""><em>none</em></MenuItem>
                      {sheetNames.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <SectionTitle>Compute columns</SectionTitle>
                  <Chip size="small" color="primary" label={`${parsedCompute.length} line item(s)`} />
                  {missingCore.length ? (
                    <Chip size="small" sx={{ bgcolor: '#fff4e5', color: AMBER, fontWeight: 600 }}
                      label={`unmapped: ${missingCore.join(', ')}`} />
                  ) : (
                    <Chip size="small" sx={{ bgcolor: '#e8f5e9', color: GREEN, fontWeight: 600 }} label="core columns mapped" />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" startIcon={<DownloadIcon />} onClick={exportParsed} disabled={!parsedCompute.length}>
                    CSV
                  </Button>
                </Stack>
                <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                  Detection handles the title rows above the header and the second Cloud Resource / Quantity pair
                  (the alternate sizing). Correct anything it got wrong below — nothing is inferred from the values.
                </Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={headSx}>
                        <TableCell>Column in workbook</TableCell>
                        <TableCell>First value</TableCell>
                        <TableCell sx={{ minWidth: 240 }}>Maps to</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {computeTable.headers.slice(0, ROW_CAP).map((h) => (
                        <TableRow key={h} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{h}</TableCell>
                          <TableCell sx={{ color: GREY, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {text((computeTable.rows[0] || {})[h])}
                          </TableCell>
                          <TableCell>
                            <TextField
                              select size="small" fullWidth value={computeMap[h] || ''}
                              onChange={(e) => setComputeMap({ ...computeMap, [h]: e.target.value })}
                            >
                              <MenuItem value=""><em>ignore</em></MenuItem>
                              {COMPUTE_TARGETS.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                            </TextField>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {storageSheet ? (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <SectionTitle>Storage columns</SectionTitle>
                    <Chip size="small" color="primary" label={`${parsedStorage.length} line item(s)`} />
                  </Stack>
                  <TableContainer sx={{ maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={headSx}>
                          <TableCell>Column in workbook</TableCell>
                          <TableCell>First value</TableCell>
                          <TableCell sx={{ minWidth: 240 }}>Maps to</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {storageTable.headers.slice(0, ROW_CAP).map((h) => (
                          <TableRow key={h} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{h}</TableCell>
                            <TableCell sx={{ color: GREY, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {text((storageTable.rows[0] || {})[h])}
                            </TableCell>
                            <TableCell>
                              <TextField
                                select size="small" fullWidth value={storageMap[h] || ''}
                                onChange={(e) => setStorageMap({ ...storageMap, [h]: e.target.value })}
                              >
                                <MenuItem value=""><em>ignore</em></MenuItem>
                                {STORAGE_TARGETS.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                              </TextField>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : null}

              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <SectionTitle>Parsed target line items</SectionTitle>
                  <Chip size="small" label={`${parsedCompute.length} compute · ${parsedStorage.length} storage`} />
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant="contained" onClick={saveCsg} disabled={!parsedCompute.length}
                    sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                  >
                    Save this CSG version
                  </Button>
                </Stack>
                {!parsedCompute.length ? (
                  <Alert severity="info">
                    No line items parsed yet. Check that the Compute Bill of Materials sheet is selected and that
                    Epic Tier / Epic Resource / Cloud Resource / Quantity are mapped.
                  </Alert>
                ) : (
                  <>
                    <TableContainer sx={{ maxHeight: 420 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={headSx}>
                            <TableCell>Epic Tier</TableCell>
                            <TableCell>Stamp</TableCell>
                            <TableCell>Epic Resource</TableCell>
                            <TableCell>Cloud Resource</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell>Alt Cloud Resource</TableCell>
                            <TableCell align="right">Alt Qty</TableCell>
                            <TableCell>OS Disk</TableCell>
                            <TableCell align="right">OS Disk GB</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parsedCompute.slice(0, ROW_CAP).map((l) => (
                            <TableRow key={l.id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{l.epicTier || DASH}</TableCell>
                              <TableCell sx={{ color: GREY }}>{l.stamp || DASH}</TableCell>
                              <TableCell>{l.epicResource || DASH}</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: NAVY }}>{l.cloudResource || DASH}</TableCell>
                              <TableCell align="right">{l.quantity === null || l.quantity === undefined ? DASH : fmt(l.quantity)}</TableCell>
                              <TableCell sx={{ color: GREY }}>{l.altCloudResource || DASH}</TableCell>
                              <TableCell align="right" sx={{ color: GREY }}>
                                {l.altQuantity === null || l.altQuantity === undefined ? DASH : fmt(l.altQuantity)}
                              </TableCell>
                              <TableCell sx={{ color: GREY }}>{l.osDiskType || DASH}</TableCell>
                              <TableCell align="right">{l.osDiskGb === null || l.osDiskGb === undefined ? DASH : fmt(l.osDiskGb)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <CapNote shown={ROW_CAP} total={parsedCompute.length} what="line items" />
                  </>
                )}
              </Paper>
            </Box>
          )}
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} />
        </Box>
      ) : null}

      {/* ============================ TAB 2 ============================ */}
      {tab === 1 ? (
        <Box>
          {!csgs.length ? (
            <EmptyState
              icon={<UploadFileIcon sx={{ fontSize: 44, color: BLUE }} />}
              title="No CSG imported yet"
              body={'Import an Epic Cloud Specifications Guide on the first tab. Until then there is no target state to '
                + 'compare the current estate against — and the current estate on its own says nothing about the Epic target.'}
              action={<Button variant="contained" onClick={() => setTab(0)} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>Go to Import CSG</Button>}
            />
          ) : (
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                  <SectionTitle>1 · Which current servers are inside the Epic scope?</SectionTitle>
                  <Box sx={{ flex: 1 }} />
                  <TextField
                    select size="small" label="Compare against" value={activeCsg ? activeCsg.id : ''}
                    onChange={(e) => setActiveCsgId(e.target.value)} sx={{ minWidth: 320 }}
                  >
                    {csgs.map((c) => <MenuItem key={c.id} value={c.id}>{csgLabel(c)}</MenuItem>)}
                  </TextField>
                </Stack>
                <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
                  The CSG only describes Epic, so the comparison is only honest against the Epic subset of the estate.
                  Declare that subset here: a server is in scope if it matches a name pattern, sits in a nominated
                  cluster or folder, or is manually added. Everything else stays out and is reported as out of scope.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth size="small" label="Name / folder contains (comma separated)"
                      value={scope.pattern}
                      onChange={(e) => setScope({ pattern: e.target.value })}
                      helperText="Matched against name, DNS name, folder, cluster and annotation"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple size="small" options={clusterOptions} value={scope.clusters || []}
                      onChange={(e, v) => setScope({ clusters: v })}
                      renderInput={(p) => <TextField {...p} label="Clusters in Epic scope" helperText={`${clusterOptions.length} cluster(s) in this workspace`} />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple size="small" options={folderOptions} value={scope.folders || []}
                      onChange={(e, v) => setScope({ folders: v })}
                      renderInput={(p) => <TextField {...p} label="Folders in Epic scope" helperText={`${folderOptions.length} folder(s) in this workspace`} />}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple size="small" options={servers} value={servers.filter((s) => (scope.include || []).indexOf(s.id) >= 0)}
                      getOptionLabel={(o) => o.name || ''}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      onChange={(e, v) => setScope({ include: v.map((x) => x.id) })}
                      filterOptions={(opts, state) => {
                        const q = lower(state.inputValue);
                        if (!q) return [];
                        const out = [];
                        for (let i = 0; i < opts.length && out.length < 50; i += 1) {
                          if (lower(opts[i].name).indexOf(q) >= 0) out.push(opts[i]);
                        }
                        return out;
                      }}
                      noOptionsText="Type to search servers"
                      renderInput={(p) => <TextField {...p} label="Always include these servers" helperText="Manual additions the patterns miss" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple size="small" options={servers} value={servers.filter((s) => (scope.exclude || []).indexOf(s.id) >= 0)}
                      getOptionLabel={(o) => o.name || ''}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      onChange={(e, v) => setScope({ exclude: v.map((x) => x.id) })}
                      filterOptions={(opts, state) => {
                        const q = lower(state.inputValue);
                        if (!q) return [];
                        const out = [];
                        for (let i = 0; i < opts.length && out.length < 50; i += 1) {
                          if (lower(opts[i].name).indexOf(q) >= 0) out.push(opts[i]);
                        }
                        return out;
                      }}
                      noOptionsText="Type to search servers"
                      renderInput={(p) => <TextField {...p} label="Always exclude these servers" helperText="False positives the patterns pick up" />}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Kpi
                      label="In Epic scope" value={fmt(inScope.length)} color={BLUE}
                      sub="the only servers the CSG speaks to"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Kpi
                      label="NOT in Epic scope" value={fmt(outScope.length)} color={AMBER}
                      sub={`${servers.length ? Math.round((outScope.length / servers.length) * 100) : 0}% of the estate — no CSG line covers these`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Kpi
                      label="Out-of-scope vCPU" value={fmt(outTotals.vcpu)} color={NAVY}
                      sub={`${fmt(outTotals.ram)} GB RAM · ${fmt(outTotals.storage)} GB storage`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                      <Typography variant="overline" sx={{ color: GREY }}>Out of scope</Typography>
                      <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                        Export the estate the CSG does not cover — the part still needing its own target state.
                      </Typography>
                      <Button size="small" startIcon={<DownloadIcon />} onClick={exportOutOfScope} disabled={!outScope.length}>
                        CSV
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small" label="Check the in-scope list" value={scopeSearch}
                    onChange={(e) => setScopeSearch(e.target.value)} sx={{ minWidth: 260 }}
                  />
                  <Chip size="small" label={`${fmt(scopePreview.total)} shown`} />
                </Stack>
                {!inScope.length ? (
                  <Alert severity="warning">
                    No servers are in Epic scope yet — widen the pattern or nominate the Epic clusters/folders.
                    Without a scope the comparison below would silently compare the CSG against nothing.
                  </Alert>
                ) : (
                  <>
                    <TableContainer sx={{ maxHeight: 260 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={headSx}>
                            <TableCell>Server</TableCell>
                            <TableCell>In scope because</TableCell>
                            <TableCell>Cluster</TableCell>
                            <TableCell>Folder</TableCell>
                            <TableCell align="right">vCPU</TableCell>
                            <TableCell align="right">RAM GB</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scopePreview.list.slice(0, ROW_CAP).map((s) => (
                            <TableRow key={s.id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                              <TableCell sx={{ color: GREY }}>{s.__why}</TableCell>
                              <TableCell sx={{ color: GREY }}>{(s.fields || {}).cluster || DASH}</TableCell>
                              <TableCell sx={{ color: GREY }}>{(s.fields || {}).folder || DASH}</TableCell>
                              <TableCell align="right">{(s.fields || {}).vcpu || DASH}</TableCell>
                              <TableCell align="right">{(s.fields || {}).ramGb || DASH}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <CapNote shown={ROW_CAP} total={scopePreview.total} what="in-scope servers" />
                  </>
                )}
              </Paper>

              <Paper sx={{ p: 2, mb: 2 }}>
                <SectionTitle sx={{ mb: 1.5 }}>2 · Current Epic footprint vs CSG target</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="overline" sx={{ color: GREY }}>Current (in Epic scope, from RVTools)</Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Servers</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: NAVY, border: 0 }}>{fmt(currentTotals.count)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Total vCPU</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: NAVY, border: 0 }}>
                              {fmt(currentTotals.vcpu)}
                              {currentTotals.missingVcpu ? <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>{currentTotals.missingVcpu} server(s) have no vCPU recorded</Typography> : null}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Total RAM (GB)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: NAVY, border: 0 }}>
                              {fmt(currentTotals.ram)}
                              {currentTotals.missingRam ? <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>{currentTotals.missingRam} server(s) have no RAM recorded</Typography> : null}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Total storage provisioned (GB)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: NAVY, border: 0 }}>
                              {fmt(currentTotals.storage)}
                              {currentTotals.missingStorage ? <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>{currentTotals.missingStorage} server(s) have no storage recorded</Typography> : null}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="overline" sx={{ color: GREY }}>
                        CSG target — {activeCsg ? `${activeCsg.label} · ${activeCsg.envKind}` : DASH}
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Target instances (sum of Quantity)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: BLUE, border: 0 }}>{fmt(targetTotals.instances)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Distinct cloud resource types</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: BLUE, border: 0 }}>{fmt(targetTotals.distinctResources)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Total vCPU</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: targetTotals.vcpu === null ? GREY : BLUE, border: 0 }}>
                              {targetTotals.vcpu === null ? DASH : fmt(targetTotals.vcpu)}
                              {targetTotals.vcpu === null ? (
                                <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>
                                  not stated in the workbook — the CSG gives instance types, not vCPU per type
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Total RAM (GB)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: targetTotals.ram === null ? GREY : BLUE, border: 0 }}>
                              {targetTotals.ram === null ? DASH : fmt(targetTotals.ram)}
                              {targetTotals.ram === null ? (
                                <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>
                                  not stated in the workbook
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>OS disk capacity (GB, Quantity applied)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: targetTotals.osDiskGb === null ? GREY : BLUE, border: 0 }}>
                              {targetTotals.osDiskGb === null ? DASH : fmt(targetTotals.osDiskGb)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: GREY, border: 0 }}>Storage BoM capacity (GB)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: targetTotals.storageGb === null ? GREY : BLUE, border: 0 }}>
                              {targetTotals.storageGb === null ? DASH : fmt(targetTotals.storageGb)}
                              {targetTotals.storageGb === null ? (
                                <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>
                                  no Storage Bill of Materials captured for this CSG
                                </Typography>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2 }}>
                  These two columns are <b>not</b> like-for-like arithmetic. The left column is measured VMware
                  capacity for the servers declared as Epic; the right is a count of cloud resources Epic specifies.
                  Anything the workbook does not state is shown as {DASH} rather than estimated.
                  {targetTotals.altInstances ? ` The workbook also carries an alternate sizing totalling ${fmt(targetTotals.altInstances)} instance(s).` : ''}
                </Alert>
              </Paper>

              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <SectionTitle>3 · Per Epic Tier</SectionTitle>
                  <Chip size="small" label={`${tierRows.length} tier row(s)`} />
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" startIcon={<DownloadIcon />} onClick={exportTiers} disabled={!tierRows.length}>CSV</Button>
                </Stack>
                <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                  Current servers are attached to a tier by matching the tier wording and the Epic Resource wording
                  against the hostname, DNS name, folder and cluster. Correct any tier by editing its match words —
                  the comparison re-runs immediately.
                </Typography>
                {!tierRows.length ? (
                  <Alert severity="info">Nothing to compare yet — import a CSG and define the Epic scope above.</Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={headSx}>
                          <TableCell>Epic Tier</TableCell>
                          <TableCell sx={{ minWidth: 180 }}>Match words</TableCell>
                          <TableCell align="right">Current servers</TableCell>
                          <TableCell align="right">Current vCPU</TableCell>
                          <TableCell align="right">Current RAM GB</TableCell>
                          <TableCell align="right">Target instances</TableCell>
                          <TableCell>CSG cloud resources</TableCell>
                          <TableCell align="right">Delta</TableCell>
                          <TableCell sx={{ minWidth: 320 }}>Gap</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tierRows.slice(0, ROW_CAP).map((r) => (
                          <TableRow key={r.tier} hover>
                            <TableCell sx={{ fontWeight: 700, color: NAVY }}>{r.tier}</TableCell>
                            <TableCell>
                              {r.targetLines ? (
                                <TextField
                                  size="small" fullWidth variant="standard"
                                  value={(db.tierRules || {})[r.tier] !== undefined ? db.tierRules[r.tier] : r.tier}
                                  onChange={(e) => setTierRule(r.tier, e.target.value)}
                                />
                              ) : <Typography variant="caption" sx={{ color: GREY }}>no CSG line</Typography>}
                            </TableCell>
                            <TableCell align="right">{fmt(r.currentCount)}</TableCell>
                            <TableCell align="right">{r.currentCount ? fmt(r.currentVcpu) : DASH}</TableCell>
                            <TableCell align="right">{r.currentCount ? fmt(r.currentRam) : DASH}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: BLUE }}>{r.targetLines ? fmt(r.targetQty) : DASH}</TableCell>
                            <TableCell sx={{ color: GREY, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Tooltip title={r.resourceList.join(', ') || ''}>
                                <span>{r.resourceList.length ? r.resourceList.join(', ') : DASH}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: r.delta === 0 ? GREEN : (r.delta > 0 ? BLUE : AMBER) }}>
                              {r.targetLines ? (r.delta > 0 ? `+${r.delta}` : r.delta) : DASH}
                            </TableCell>
                            <TableCell sx={{ color: GREY }}>{r.gap}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <CapNote shown={ROW_CAP} total={tierRows.length} what="tier rows" />
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                      <SectionTitle>Finding A · Epic servers with no CSG line</SectionTitle>
                      <Chip size="small" sx={{ bgcolor: '#fff4e5', color: AMBER, fontWeight: 700 }} label={fmt(unmatchedServers.length)} />
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" startIcon={<DownloadIcon />} onClick={exportUnmatched} disabled={!unmatchedServers.length}>CSV</Button>
                    </Stack>
                    <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                      In the declared Epic scope today, but nothing in this CSG accounts for them. Each one is either
                      genuinely missing from the target state, or was wrongly scoped as Epic — both need an answer
                      before the CSG can be signed off.
                    </Typography>
                    {!unmatchedServers.length ? (
                      <Alert severity="success">Every in-scope Epic server matched a CSG line.</Alert>
                    ) : (
                      <>
                        <TableContainer sx={{ maxHeight: 420 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow sx={headSx}>
                                <TableCell>Server</TableCell>
                                <TableCell align="right">vCPU</TableCell>
                                <TableCell align="right">RAM GB</TableCell>
                                <TableCell>Why no line</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {unmatchedServers.slice(0, ROW_CAP).map((s) => (
                                <TableRow key={s.id} hover>
                                  <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                                  <TableCell align="right">{(s.fields || {}).vcpu || DASH}</TableCell>
                                  <TableCell align="right">{(s.fields || {}).ramGb || DASH}</TableCell>
                                  <TableCell sx={{ color: GREY }}>{s.__reason}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <CapNote shown={ROW_CAP} total={unmatchedServers.length} what="servers" />
                      </>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
                      <SectionTitle>Finding B · CSG lines with no current equivalent</SectionTitle>
                      <Chip size="small" sx={{ bgcolor: '#e3f2fd', color: BLUE, fontWeight: 700 }} label={fmt(newBuildLines.length)} />
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" startIcon={<DownloadIcon />} onClick={exportNewBuild} disabled={!newBuildLines.length}>CSV</Button>
                    </Stack>
                    <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                      Specified in the target state with nothing matched in the current estate — new build, and the
                      part of the programme with no migration source to lift from. {fmt(matchedLines)} of{' '}
                      {fmt(activeLines.length)} CSG line(s) did match something current.
                    </Typography>
                    {!newBuildLines.length ? (
                      <Alert severity="success">Every CSG line matched at least one current server.</Alert>
                    ) : (
                      <>
                        <TableContainer sx={{ maxHeight: 420 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow sx={headSx}>
                                <TableCell>Epic Tier</TableCell>
                                <TableCell>Epic Resource</TableCell>
                                <TableCell>Cloud Resource</TableCell>
                                <TableCell align="right">Qty</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {newBuildLines.slice(0, ROW_CAP).map((l) => (
                                <TableRow key={l.id} hover>
                                  <TableCell sx={{ fontWeight: 600 }}>{l.epicTier || DASH}</TableCell>
                                  <TableCell>{l.epicResource || DASH}</TableCell>
                                  <TableCell sx={{ color: NAVY, fontWeight: 600 }}>{l.cloudResource || DASH}</TableCell>
                                  <TableCell align="right">{l.quantity === null || l.quantity === undefined ? DASH : fmt(l.quantity)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <CapNote shown={ROW_CAP} total={newBuildLines.length} what="CSG lines" />
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      ) : null}

      {/* ============================ TAB 3 ============================ */}
      {tab === 2 ? (
        <Box>
          {!csgs.length ? (
            <EmptyState
              icon={<UploadFileIcon sx={{ fontSize: 44, color: BLUE }} />}
              title="No CSG versions stored yet"
              body={'Import a CSG workbook on the first tab. When Epic reissues it, import the new one too and this tab '
                + 'will show exactly which lines were added, removed or resized between the two drafts.'}
              action={<Button variant="contained" onClick={() => setTab(0)} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>Go to Import CSG</Button>}
            />
          ) : (
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <SectionTitle sx={{ mb: 1.5 }}>Imported CSG versions</SectionTitle>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={headSx}>
                        <TableCell>Label</TableCell>
                        <TableCell>Environment</TableCell>
                        <TableCell>Imported</TableCell>
                        <TableCell>File</TableCell>
                        <TableCell align="right">Compute lines</TableCell>
                        <TableCell align="right">Target instances</TableCell>
                        <TableCell align="right">Storage lines</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csgs.slice(0, ROW_CAP).map((c) => {
                        const t = csgTotals(c);
                        return (
                          <TableRow key={c.id} hover>
                            <TableCell sx={{ fontWeight: 700, color: NAVY }}>{c.label}</TableCell>
                            <TableCell>
                              <Chip size="small" variant="outlined" label={c.envKind} />
                            </TableCell>
                            <TableCell sx={{ color: GREY }}>{String(c.importedAt).slice(0, 16).replace('T', ' ')}</TableCell>
                            <TableCell sx={{ color: GREY }}>{c.fileName || DASH}</TableCell>
                            <TableCell align="right">{fmt(t.lineCount)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: BLUE }}>{fmt(t.instances)}</TableCell>
                            <TableCell align="right">{fmt(t.storageLineCount)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Remove this CSG version">
                                <IconButton size="small" onClick={() => deleteCsg(c.id)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <CapNote shown={ROW_CAP} total={csgs.length} what="CSG versions" />
              </Paper>

              <Paper sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                  <SectionTitle>What changed between two CSGs</SectionTitle>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" startIcon={<DownloadIcon />} onClick={exportDiff} disabled={!diff || !diffRows.length}>CSV</Button>
                </Stack>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={5}>
                    <TextField select fullWidth size="small" label="Previous CSG" value={leftId} onChange={(e) => setLeftId(e.target.value)}>
                      {csgs.map((c) => <MenuItem key={c.id} value={c.id}>{csgLabel(c)}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField select fullWidth size="small" label="Newer CSG" value={rightId} onChange={(e) => setRightId(e.target.value)}>
                      {csgs.map((c) => <MenuItem key={c.id} value={c.id}>{csgLabel(c)}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth size="small" variant={showUnchanged ? 'contained' : 'outlined'}
                      onClick={() => setShowUnchanged(!showUnchanged)}
                      sx={showUnchanged ? { bgcolor: BLUE, '&:hover': { bgcolor: NAVY } } : undefined}
                    >
                      {showUnchanged ? 'Hide unchanged' : 'Show unchanged'}
                    </Button>
                  </Grid>
                </Grid>

                {!diff ? (
                  <Alert severity="info">
                    {csgs.length < 2
                      ? 'Only one CSG is stored. Import the reissued workbook and the line-by-line diff appears here.'
                      : 'Pick two different CSG versions to compare.'}
                  </Alert>
                ) : (
                  <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6} md={3}><Kpi label="Added lines" value={fmt(diff.added)} color={GREEN} /></Grid>
                      <Grid item xs={6} md={3}><Kpi label="Removed lines" value={fmt(diff.removed)} color={AMBER} /></Grid>
                      <Grid item xs={6} md={3}><Kpi label="Quantity changed" value={fmt(diff.modified)} color={BLUE} /></Grid>
                      <Grid item xs={6} md={3}>
                        <Kpi
                          label="Target instance delta"
                          value={(() => {
                            const d = csgTotals(rightCsg).instances - csgTotals(leftCsg).instances;
                            return d > 0 ? `+${fmt(d)}` : fmt(d);
                          })()}
                          color={NAVY}
                          sub={`${leftCsg.label} → ${rightCsg.label}`}
                        />
                      </Grid>
                    </Grid>
                    {!diffRows.length ? (
                      <Alert severity="success">No differences between these two CSG versions.</Alert>
                    ) : (
                      <>
                        <TableContainer sx={{ maxHeight: 520 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow sx={headSx}>
                                <TableCell>Status</TableCell>
                                <TableCell>Epic Tier</TableCell>
                                <TableCell>Stamp</TableCell>
                                <TableCell>Epic Resource</TableCell>
                                <TableCell>Cloud Resource</TableCell>
                                <TableCell align="right">{leftCsg.label}</TableCell>
                                <TableCell align="right">{rightCsg.label}</TableCell>
                                <TableCell align="right">Delta</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {diffRows.slice(0, ROW_CAP).map((r) => (
                                <TableRow key={r.key} hover>
                                  <TableCell>
                                    <Chip
                                      size="small" label={r.status}
                                      sx={{
                                        fontWeight: 700,
                                        bgcolor: r.status === 'Added' ? '#e8f5e9' : r.status === 'Removed' ? '#fff4e5' : r.status === 'Changed' ? '#e3f2fd' : '#f0f2f5',
                                        color: r.status === 'Added' ? GREEN : r.status === 'Removed' ? AMBER : r.status === 'Changed' ? BLUE : GREY,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{r.epicTier || DASH}</TableCell>
                                  <TableCell sx={{ color: GREY }}>{r.stamp || DASH}</TableCell>
                                  <TableCell>{r.epicResource || DASH}</TableCell>
                                  <TableCell sx={{ color: NAVY, fontWeight: 600 }}>{r.cloudResource || DASH}</TableCell>
                                  <TableCell align="right">{r.prevQty === null ? DASH : fmt(r.prevQty)}</TableCell>
                                  <TableCell align="right">{r.nextQty === null ? DASH : fmt(r.nextQty)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, color: r.delta > 0 ? GREEN : r.delta < 0 ? AMBER : GREY }}>
                                    {r.delta > 0 ? `+${fmt(r.delta)}` : fmt(r.delta)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <CapNote shown={ROW_CAP} total={diffRows.length} what="diff rows" />
                      </>
                    )}
                  </>
                )}
              </Paper>
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
